import { useEffect, useRef, useState } from 'react';
import { useEditorStore, type EditorEntityData } from '../../stores/editorStore';
import { resolveDecorationImage, resolveEntityImage } from '../../utils/assetPreview';

const LEVEL_IDS = ['level1', 'level2', 'level3', 'level4', 'level5', 'level6', 'level7'];

type EntitiesFile = {
  entities?: EditorEntityData[];
  additions?: {
    entities?: EditorEntityData[];
  };
};

async function parseJsonSafe<T>(response: Response): Promise<T | null> {
  if (!response.ok) return null;
  const raw = await response.text();
  const trimmed = raw.trim();
  if (!trimmed || trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html')) return null;

  try {
    return JSON.parse(trimmed) as T;
  } catch {
    return null;
  }
}

export default function PalettePanel() {
  const [expanded, setExpanded] = useState(true);
  const [allLevelEntities, setAllLevelEntities] = useState<EditorEntityData[]>([]);
  const [isLoadingAllEntities, setIsLoadingAllEntities] = useState(false);
  const suppressClickRef = useRef(false);
  const { mode, entityPaletteScope, levelData, entities, setPaletteInspectorSelection } =
    useEditorStore();

  useEffect(() => {
    if (entityPaletteScope !== 'all') return;

    let cancelled = false;

    const loadAllLevelEntities = async () => {
      setIsLoadingAllEntities(true);
      try {
        const files = await Promise.all(
          LEVEL_IDS.flatMap((levelId) => [
            fetch(`/assets/levels/${levelId}-entities-c3data.json`).then((response) =>
              parseJsonSafe<EntitiesFile>(response)
            ),
            fetch(`/assets/levels/${levelId}-entities-overrides.json`).then((response) =>
              parseJsonSafe<EntitiesFile>(response)
            ),
          ])
        );

        const merged = files.flatMap((file) => [
          ...(file?.entities ?? []),
          ...(file?.additions?.entities ?? []),
        ]);

        if (!cancelled) {
          setAllLevelEntities(merged);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingAllEntities(false);
        }
      }
    };

    loadAllLevelEntities();

    return () => {
      cancelled = true;
    };
  }, [entityPaletteScope]);

  const handlePointerDragStart = (e: React.PointerEvent<HTMLDivElement>, payload: unknown) => {
    if (e.button !== 0) return;

    const sourceCard = e.currentTarget;
    const startX = e.clientX;
    const startY = e.clientY;
    let hasDragged = false;
    let preview: HTMLDivElement | null = null;

    const createPreview = () => {
      const nextPreview = document.createElement('div');
      const image = sourceCard.querySelector('img')?.cloneNode(true) as HTMLImageElement | null;

      nextPreview.style.position = 'fixed';
      nextPreview.style.left = `${startX}px`;
      nextPreview.style.top = `${startY}px`;
      nextPreview.style.width = '72px';
      nextPreview.style.height = '72px';
      nextPreview.style.display = 'flex';
      nextPreview.style.alignItems = 'center';
      nextPreview.style.justifyContent = 'center';
      nextPreview.style.pointerEvents = 'none';
      nextPreview.style.zIndex = '9999';
      nextPreview.style.transform = 'translate(-50%, -50%)';
      nextPreview.style.opacity = '0.9';

      if (image) {
        image.style.width = '64px';
        image.style.height = '64px';
        image.style.objectFit = 'contain';
        image.style.imageRendering = 'pixelated';
        nextPreview.appendChild(image);
      }

      document.body.appendChild(nextPreview);
      return nextPreview;
    };

    const handlePointerMove = (event: PointerEvent) => {
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      if (!hasDragged && Math.hypot(dx, dy) < 4) return;

      if (!hasDragged) {
        hasDragged = true;
        suppressClickRef.current = true;
        document.body.style.userSelect = 'none';
        preview = createPreview();
      }

      if (preview) {
        preview.style.left = `${event.clientX}px`;
        preview.style.top = `${event.clientY}px`;
      }
    };

    const handlePointerUp = (event: PointerEvent) => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      document.body.style.userSelect = '';
      preview?.remove();

      if (!hasDragged) return;

      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);

      const canvas = document.querySelector('.editor-canvas');
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const isInsideCanvas =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!isInsideCanvas) return;

      window.__imbasionEditorDropFromPalette?.(payload, event.clientX, event.clientY);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  // Get unique object types from current level
  const objectTypes = levelData
    ? Array.from(
        new Set(levelData.layers.flatMap((layer) => layer.objects.map((obj) => obj.type)))
      ).sort()
    : [];

  const paletteEntities =
    entityPaletteScope === 'all'
      ? allLevelEntities.map((data, index) => ({ id: `all_${index}`, data }))
      : entities;

  const entityTypes = Array.from(
    new Set(
      paletteEntities
        .map((entity) => (entity.data.texture || entity.data.entityId || '').toLowerCase())
        .filter(Boolean)
    )
  ).sort();

  const entityPaletteItems = entityTypes.map((type) => {
    const template = paletteEntities.find(
      (entity) => (entity.data.texture || entity.data.entityId || '').toLowerCase() === type
    )?.data;
    return {
      type,
      payload: {
        kind: 'entity' as const,
        data: {
          entityId: template?.entityId ?? type,
          texture: template?.texture ?? type,
          width: template?.width ?? 64,
          height: template?.height ?? 64,
          originX: (template as { originX?: number } | undefined)?.originX,
          originY: (template as { originY?: number } | undefined)?.originY,
          properties: template?.properties,
        },
      },
    };
  });

  const layoutPaletteItems = objectTypes.map((type) => {
    const templateLayer = levelData?.layers.find((layer) =>
      layer.objects.some((obj) => obj.type === type)
    );
    const template = templateLayer?.objects.find((obj) => obj.type === type);

    return {
      type,
      payload: {
        kind: 'layout' as const,
        data: {
          type,
          layerName: templateLayer?.name,
          width: template?.width ?? 64,
          height: template?.height ?? 64,
          originX: template?.originX,
          originY: template?.originY,
          angle: template?.angle,
        },
      },
    };
  });

  return (
    <div className="panel">
      <div className="panel-header" onClick={() => setExpanded(!expanded)}>
        <span>Palette</span>
        <span>{expanded ? '▼' : '▶'}</span>
      </div>
      {expanded && (
        <div className="panel-content expanded">
          {!levelData ? (
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              Load a level first
            </p>
          ) : mode === 'entities' && entityPaletteScope === 'all' ? (
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              All entities are shown in the main view.
            </p>
          ) : mode === 'entities' ? (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              <p style={{ marginBottom: 8 }}>
                {entityPaletteScope === 'all' ? 'All game entities' : 'Available entities'} (
                {entityTypes.length}):
              </p>
              {isLoadingAllEntities ? (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                  Loading entities...
                </p>
              ) : entityTypes.length === 0 ? (
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                  No entities loaded for this level
                </p>
              ) : (
                <div className="palette-entity-list">
                  {entityPaletteItems.map((item) => (
                    <div
                      key={item.type}
                      className="palette-entity-card"
                      title={item.type}
                      draggable={false}
                      onPointerDown={(e) => handlePointerDragStart(e, item.payload)}
                      onClick={(e) => {
                        if (suppressClickRef.current) {
                          e.preventDefault();
                          e.stopPropagation();
                          return;
                        }

                        setPaletteInspectorSelection({
                          kind: 'entity',
                          type: item.type,
                          width: item.payload.data.width,
                          height: item.payload.data.height,
                          animation:
                            typeof item.payload.data.properties?.animation === 'string'
                              ? item.payload.data.properties.animation
                              : undefined,
                        });
                      }}
                    >
                      <div className="palette-entity-thumb-wrap">
                        <img
                          src={resolveEntityImage(item.type)}
                          alt={item.type}
                          className="palette-entity-thumb"
                          loading="lazy"
                          draggable={false}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                      <span className="palette-entity-name">{item.type}</span>
                      <span className="palette-entity-meta">
                        {item.payload.data.width} x {item.payload.data.height}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
              <p style={{ marginBottom: 8 }}>Available types ({objectTypes.length}):</p>
              <div className="palette-entity-list">
                {layoutPaletteItems.map((item) => (
                  <div
                    key={item.type}
                    className="palette-entity-card"
                    title={item.type}
                    draggable={false}
                    onPointerDown={(e) => handlePointerDragStart(e, item.payload)}
                    onClick={(e) => {
                      if (suppressClickRef.current) {
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                      }

                      setPaletteInspectorSelection({
                        kind: 'layout',
                        type: item.type,
                        width: item.payload.data.width,
                        height: item.payload.data.height,
                      });
                    }}
                  >
                    <div className="palette-entity-thumb-wrap">
                      <img
                        src={resolveDecorationImage(item.type)}
                        alt={item.type}
                        className="palette-entity-thumb"
                        loading="lazy"
                        draggable={false}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                    <span className="palette-entity-name">{item.type}</span>
                    <span className="palette-entity-meta">
                      {item.payload.data.width} x {item.payload.data.height}
                    </span>
                  </div>
                ))}
              </div>
              <p style={{ marginTop: 8, fontStyle: 'italic' }}>
                Drag cards to the map to create new objects
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
